import type { AaguidCatalog } from '@newcore/ui/passkey';
import { FACTOR_ERROR_MESSAGES } from '@/features/onboarding/constants/onboarding.constants';
import {
  ChallengeStatus,
  type CurrentStepResponse,
  type FactorType,
} from '@/features/onboarding/types/onboarding.types';
import { webAuthnRejectionMessage } from '@/utils/webauthn';

interface FactorErrorMessageParams {
  factorType: FactorType;
  challengeStatus: ChallengeStatus;
  metadata: CurrentStepResponse['metadata'] | undefined;
  catalog: AaguidCatalog | undefined;
}

/**
 * Get user-friendly error message for a factor verification failure
 */
export function getFactorErrorMessage({
  factorType,
  challengeStatus,
  metadata,
  catalog,
}: FactorErrorMessageParams): string {
  const messages = FACTOR_ERROR_MESSAGES[factorType];

  // A policy rejection names the reason; the canned lines below would
  // otherwise report "too many failed attempts" for a first attempt.
  const rejection = webAuthnRejectionMessage(
    metadata?.error_code,
    metadata?.aaguid,
    catalog
  );

  if (rejection) {
    return rejection;
  }

  if (!messages) {
    return 'Verification failed. Please try again.';
  }

  if (challengeStatus === ChallengeStatus.FAILED) {
    return messages.maxRetries;
  }

  if (challengeStatus === ChallengeStatus.RETRY_REQUIRED) {
    return messages.invalidCode;
  }

  return messages.generic;
}
